import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Newspaper, Search, Edit2, Trash2, Key, Mail, Phone, MapPin,
  Loader2, Check, X, Eye, BarChart3, Calendar, Clock, 
  CheckCircle2, XCircle, AlertCircle, ExternalLink, Globe,
  Instagram, Twitter, Youtube, DollarSign, Users, ShieldCheck,
  Plus, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const STATUS_CONFIG = {
  pending: { label: 'Pendiente', icon: Clock, color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30' },
  approved: { label: 'Aprobado', icon: CheckCircle2, color: 'text-green-400 bg-green-500/10 border-green-500/30' },
  rejected: { label: 'Rechazado', icon: XCircle, color: 'text-red-400 bg-red-500/10 border-red-500/30' },
  contacted: { label: 'Contactado', icon: Mail, color: 'text-blue-400 bg-blue-500/10 border-blue-500/30' },
};

const TABS = [
  { id: 'press', label: 'Medios Activos', icon: Newspaper },
  { id: 'applications', label: 'Solicitudes', icon: AlertCircle },
];

const PressAdminPage = () => {
  const [pressList, setPressList] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('applications');
  const [selectedApp, setSelectedApp] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showApplicationDetail, setShowApplicationDetail] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [approving, setApproving] = useState(false);
  const [selectedPress, setSelectedPress] = useState(null);
  
  const [approvalData, setApprovalData] = useState({
    slug: '',
    service_fee_percent: 10,
    is_verified: false
  });

  const [createFormData, setCreateFormData] = useState({
    media_name: '',
    contact_name: '',
    email: '',
    phone: '',
    province: '',
    city: '',
    media_type: 'instagram',
    description: '',
    service_fee_percent: 10,
    is_verified: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [pressRes, appsRes] = await Promise.all([
        api.get('/press/admin/all').catch(() => ({ data: [] })),
        api.get('/press/admin/applications').catch(() => ({ data: [] }))
      ]);
      setPressList(pressRes.data || []);
      setApplications(appsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleApprove = async () => {
    if (!selectedApp || !approvalData.slug) return;
    
    setApproving(true);
    try {
      const response = await api.post(`/press/admin/applications/${selectedApp.id}/approve`, approvalData);
      toast.success(`Medio aprobado. Contraseña temporal: ${response.data.temp_password}`);
      setNewPassword(response.data.temp_password);
      setShowApproveModal(false);
      setShowPasswordModal(true);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aprobar');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!selectedApp) return;
    
    try {
      await api.post(`/press/admin/applications/${selectedApp.id}/reject`, {
        reason: rejectionReason
      });
      toast.success('Solicitud rechazada');
      setShowRejectModal(false);
      setRejectionReason('');
      fetchData();
    } catch (error) {
      toast.error('Error al rechazar');
    }
  };

  const handleCreatePress = async () => {
    try {
      const response = await api.post('/press/admin/create', createFormData);
      toast.success(`Medio creado. Contraseña temporal: ${response.data.temp_password}`);
      setNewPassword(response.data.temp_password);
      setShowCreateModal(false);
      setShowPasswordModal(true);
      fetchData();
      setCreateFormData({
        media_name: '',
        contact_name: '',
        email: '',
        phone: '',
        province: '',
        city: '',
        media_type: 'instagram',
        description: '',
        service_fee_percent: 10,
        is_verified: false
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear medio');
    }
  };

  const handleResetPassword = async (pressId) => {
    try {
      const response = await api.post(`/press/admin/${pressId}/reset-password`);
      setNewPassword(response.data.new_password);
      setShowPasswordModal(true);
      toast.success('Contraseña reseteada');
    } catch (error) {
      toast.error('Error al resetear contraseña');
    }
  };

  const handleToggleActive = async (pressId) => {
    try {
      const response = await api.post(`/press/admin/${pressId}/toggle-active`);
      toast.success(response.data.is_active ? 'Medio activado' : 'Medio desactivado');
      fetchData();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleViewDetails = async (pressId) => {
    try {
      const response = await api.get(`/press/admin/${pressId}/details`);
      setSelectedPress(response.data);
    } catch (error) {
      toast.error('Error al cargar detalles');
    }
  };

  const handleDeletePress = async (pressId) => {
    if (!window.confirm('¿Estás seguro de eliminar este medio?')) return;
    
    try {
      await api.delete(`/press/admin/${pressId}`);
      toast.success('Medio eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const filteredApplications = applications.filter(app =>
    app.media_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredPress = pressList.filter(p =>
    p.media_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const pendingCount = applications.filter(a => a.status === 'pending').length;

  return (
    <SidebarProvider>
      <AdminLayout>
        <div className="space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-black text-white flex items-center gap-3">
                <Newspaper className="text-blue-400" />
                Gestión de Prensa
              </h1>
              <p className="text-zinc-400 text-sm mt-1">
                Administrá medios de prensa y solicitudes
              </p>
            </div>
            <Button onClick={() => setShowCreateModal(true)} className="bg-blue-500 hover:bg-blue-600">
              <Plus size={18} className="mr-2" />
              Nuevo Medio
            </Button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 border-b border-zinc-800 pb-2">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  activeTab === tab.id
                    ? 'bg-blue-500/20 text-blue-400'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
              >
                <tab.icon size={18} />
                {tab.label}
                {tab.id === 'applications' && pendingCount > 0 && (
                  <span className="bg-yellow-500 text-black text-xs font-bold px-2 py-0.5 rounded-full">
                    {pendingCount}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por nombre o email..."
              className="pl-10 bg-zinc-900 border-zinc-800"
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="animate-spin text-blue-400" size={32} />
            </div>
          ) : (
            <>
              {/* Applications Tab */}
              {activeTab === 'applications' && (
                <div className="space-y-4">
                  {filteredApplications.length === 0 ? (
                    <div className="text-center py-20 text-zinc-500">
                      No hay solicitudes
                    </div>
                  ) : (
                    filteredApplications.map((app) => {
                      const status = STATUS_CONFIG[app.status] || STATUS_CONFIG.pending;
                      const StatusIcon = status.icon;
                      const totalFollowers = app.social_networks?.reduce((sum, sn) => sum + (sn.followers || 0), 0) || 0;
                      
                      return (
                        <motion.div
                          key={app.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5"
                        >
                          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                                <Newspaper className="text-blue-400" size={24} />
                              </div>
                              <div>
                                <h3 className="text-white font-bold">{app.media_name}</h3>
                                <div className="flex flex-wrap items-center gap-3 text-sm text-zinc-400 mt-1">
                                  <span className="flex items-center gap-1">
                                    <Mail size={14} />
                                    {app.email}
                                  </span>
                                  {app.phone && (
                                    <span className="flex items-center gap-1">
                                      <Phone size={14} />
                                      {app.phone}
                                    </span>
                                  )}
                                  {totalFollowers > 0 && (
                                    <span className="flex items-center gap-1">
                                      <Users size={14} />
                                      {formatNumber(totalFollowers)} seguidores
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 mt-2">
                                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border ${status.color}`}>
                                    <StatusIcon size={12} />
                                    {status.label}
                                  </span>
                                  <span className="text-xs text-zinc-500">
                                    {new Date(app.created_at).toLocaleDateString()}
                                  </span>
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedApp(app);
                                  setShowApplicationDetail(true);
                                }}
                                className="text-zinc-400 hover:text-white"
                              >
                                <Eye size={16} className="mr-1" />
                                Ver Detalle
                              </Button>
                              
                              {app.status === 'pending' && (
                                <>
                                  <Button
                                    size="sm"
                                    onClick={() => {
                                      setSelectedApp(app);
                                      setApprovalData({
                                        slug: generateSlug(app.media_name),
                                        service_fee_percent: 10,
                                        is_verified: false
                                      });
                                      setShowApproveModal(true);
                                    }}
                                    className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                                  >
                                    <Check size={16} className="mr-1" />
                                    Aprobar
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      setSelectedApp(app);
                                      setShowRejectModal(true);
                                    }}
                                    className="text-red-400 hover:bg-red-500/10"
                                  >
                                    <X size={16} />
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      );
                    })
                  )}
                </div>
              )}

              {/* Active Press Tab */}
              {activeTab === 'press' && (
                <div className="space-y-4">
                  {filteredPress.length === 0 ? (
                    <div className="text-center py-20 text-zinc-500">
                      No hay medios de prensa activos
                    </div>
                  ) : (
                    filteredPress.map((press) => {
                      const totalFollowers = press.social_networks?.reduce((sum, sn) => sum + (sn.followers || 0), 0) || 0;
                      
                      return (
                        <motion.div
                          key={press.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5"
                          data-testid={`press-card-${press.id}`}
                        >
                          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
                                {press.logo_url ? (
                                  <img src={press.logo_url} alt={press.media_name} className="w-full h-full object-cover" />
                                ) : (
                                  <Newspaper className="text-blue-400" size={24} />
                                )}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <h3 className="text-white font-bold">{press.media_name}</h3>
                                  {press.is_verified && (
                                    <ShieldCheck className="text-blue-400" size={16} />
                                  )}
                                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                                    press.is_active !== false
                                      ? 'bg-green-500/20 text-green-400' 
                                      : 'bg-red-500/20 text-red-400'
                                  }`}>
                                    {press.is_active !== false ? 'Activo' : 'Inactivo'}
                                  </span>
                                </div>
                                <div className="flex flex-wrap items-center gap-3 text-sm text-zinc-400 mt-1">
                                  <span className="flex items-center gap-1">
                                    <Mail size={14} />
                                    {press.email}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Users size={14} />
                                    {formatNumber(totalFollowers)} seguidores
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Eye size={14} />
                                    {formatNumber(press.profile_views || 0)} vistas
                                  </span>
                                </div>
                                <p className="text-xs text-zinc-500 mt-1">
                                  Slug: /{press.slug} | Fee: {press.service_fee_percent}%
                                </p>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewDetails(press.id)}
                                className="text-zinc-400 hover:text-white"
                                title="Ver detalles"
                                data-testid={`view-details-${press.id}`}
                              >
                                <Eye size={16} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleActive(press.id)}
                                className={press.is_active !== false ? 'text-green-400 hover:text-red-400' : 'text-red-400 hover:text-green-400'}
                                title={press.is_active !== false ? 'Desactivar' : 'Activar'}
                                data-testid={`toggle-active-${press.id}`}
                              >
                                {press.is_active !== false ? <Check size={16} /> : <X size={16} />}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleResetPassword(press.id)}
                                className="text-zinc-400 hover:text-white"
                                title="Resetear contraseña"
                              >
                                <Key size={16} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeletePress(press.id)}
                                className="text-red-400 hover:bg-red-500/10"
                                title="Eliminar"
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Approve Modal */}
        <Dialog open={showApproveModal} onOpenChange={setShowApproveModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-white">Aprobar Solicitud</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-zinc-400">Medio</Label>
                <p className="text-white font-bold">{selectedApp?.media_name}</p>
              </div>
              <div>
                <Label className="text-zinc-400">Slug (URL)</Label>
                <Input
                  value={approvalData.slug}
                  onChange={(e) => setApprovalData({ ...approvalData, slug: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="nombre-del-medio"
                />
                <p className="text-xs text-zinc-500 mt-1">URL: /prensa/{approvalData.slug}</p>
              </div>
              <div>
                <Label className="text-zinc-400">Costo Servicio (%)</Label>
                <Input
                  type="number"
                  value={approvalData.service_fee_percent}
                  onChange={(e) => setApprovalData({ ...approvalData, service_fee_percent: parseFloat(e.target.value) || 10 })}
                  className="bg-zinc-800 border-zinc-700"
                  min="0"
                  max="100"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="verified"
                  checked={approvalData.is_verified}
                  onChange={(e) => setApprovalData({ ...approvalData, is_verified: e.target.checked })}
                  className="rounded bg-zinc-800 border-zinc-700"
                />
                <Label htmlFor="verified" className="text-zinc-400">Medio verificado</Label>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowApproveModal(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleApprove} 
                disabled={approving || !approvalData.slug}
                className="bg-green-500 hover:bg-green-600"
              >
                {approving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                Aprobar
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Reject Modal */}
        <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-white">Rechazar Solicitud</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-zinc-400">Medio</Label>
                <p className="text-white font-bold">{selectedApp?.media_name}</p>
              </div>
              <div>
                <Label className="text-zinc-400">Motivo del rechazo (opcional)</Label>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="Explicá el motivo..."
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowRejectModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleReject} className="bg-red-500 hover:bg-red-600">
                Rechazar
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Create Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">Crear Medio de Prensa</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400">Nombre del Medio *</Label>
                  <Input
                    value={createFormData.media_name}
                    onChange={(e) => setCreateFormData({ ...createFormData, media_name: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Email *</Label>
                  <Input
                    type="email"
                    value={createFormData.email}
                    onChange={(e) => setCreateFormData({ ...createFormData, email: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Contacto</Label>
                  <Input
                    value={createFormData.contact_name}
                    onChange={(e) => setCreateFormData({ ...createFormData, contact_name: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Teléfono</Label>
                  <Input
                    value={createFormData.phone}
                    onChange={(e) => setCreateFormData({ ...createFormData, phone: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Tipo de Medio</Label>
                  <select
                    value={createFormData.media_type}
                    onChange={(e) => setCreateFormData({ ...createFormData, media_type: e.target.value })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-white"
                  >
                    <option value="instagram">Instagram</option>
                    <option value="tiktok">TikTok</option>
                    <option value="multi-platform">Multi-plataforma</option>
                    <option value="blog">Blog/Web</option>
                    <option value="radio">Radio</option>
                    <option value="youtube">YouTube</option>
                  </select>
                </div>
                <div>
                  <Label className="text-zinc-400">Costo Servicio (%)</Label>
                  <Input
                    type="number"
                    value={createFormData.service_fee_percent}
                    onChange={(e) => setCreateFormData({ ...createFormData, service_fee_percent: parseFloat(e.target.value) || 10 })}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
              </div>
              <div>
                <Label className="text-zinc-400">Descripción</Label>
                <Textarea
                  value={createFormData.description}
                  onChange={(e) => setCreateFormData({ ...createFormData, description: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="createVerified"
                  checked={createFormData.is_verified}
                  onChange={(e) => setCreateFormData({ ...createFormData, is_verified: e.target.checked })}
                  className="rounded bg-zinc-800 border-zinc-700"
                />
                <Label htmlFor="createVerified" className="text-zinc-400">Medio verificado</Label>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowCreateModal(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleCreatePress}
                disabled={!createFormData.media_name || !createFormData.email}
                className="bg-blue-500 hover:bg-blue-600"
              >
                Crear Medio
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Password Modal */}
        <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-white">Credenciales de Acceso</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <div className="bg-zinc-800 rounded-lg p-4 text-center">
                <p className="text-zinc-400 text-sm mb-2">Contraseña temporal:</p>
                <p className="text-2xl font-mono text-white font-bold select-all">{newPassword}</p>
              </div>
              <p className="text-zinc-500 text-sm mt-4 text-center">
                Copiá esta contraseña y enviala al medio de prensa. 
                Deberá cambiarla en su primer inicio de sesión.
              </p>
            </div>
            <div className="flex justify-end">
              <Button onClick={() => {
                navigator.clipboard.writeText(newPassword);
                toast.success('Contraseña copiada');
              }}>
                Copiar y Cerrar
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Application Detail Modal */}
        <Dialog open={showApplicationDetail} onOpenChange={setShowApplicationDetail}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Detalle de Solicitud</DialogTitle>
            </DialogHeader>
            {selectedApp && (
              <div className="space-y-6 py-4">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-500 text-xs">Nombre del Medio</Label>
                    <p className="text-white">{selectedApp.media_name}</p>
                  </div>
                  <div>
                    <Label className="text-zinc-500 text-xs">Tipo</Label>
                    <p className="text-white capitalize">{selectedApp.media_type}</p>
                  </div>
                  <div>
                    <Label className="text-zinc-500 text-xs">Email</Label>
                    <p className="text-white">{selectedApp.email}</p>
                  </div>
                  <div>
                    <Label className="text-zinc-500 text-xs">Teléfono</Label>
                    <p className="text-white">{selectedApp.phone || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-zinc-500 text-xs">Contacto</Label>
                    <p className="text-white">{selectedApp.contact_name || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-zinc-500 text-xs">Ubicación</Label>
                    <p className="text-white">{selectedApp.city || selectedApp.province || '-'}</p>
                  </div>
                </div>

                {selectedApp.description && (
                  <div>
                    <Label className="text-zinc-500 text-xs">Descripción</Label>
                    <p className="text-white text-sm">{selectedApp.description}</p>
                  </div>
                )}

                {/* Social Networks */}
                {selectedApp.social_networks?.length > 0 && (
                  <div>
                    <Label className="text-zinc-500 text-xs mb-2 block">Redes Sociales</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {selectedApp.social_networks.map((sn, i) => (
                        <div key={i} className="bg-zinc-800 rounded-lg p-3 flex items-center gap-3">
                          <Globe className="text-zinc-400" size={16} />
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm capitalize">{sn.platform}</p>
                            <p className="text-zinc-500 text-xs truncate">{sn.username || sn.url}</p>
                          </div>
                          <p className="text-white font-bold text-sm">{formatNumber(sn.followers)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Audience */}
                {selectedApp.audience && (
                  <div>
                    <Label className="text-zinc-500 text-xs mb-2 block">Audiencia</Label>
                    <div className="bg-zinc-800 rounded-lg p-4 grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-white font-bold">{selectedApp.audience.age_range_min}-{selectedApp.audience.age_range_max}</p>
                        <p className="text-zinc-500 text-xs">Rango de edad</p>
                      </div>
                      <div>
                        <p className="text-white font-bold capitalize">{selectedApp.audience.gender_distribution}</p>
                        <p className="text-zinc-500 text-xs">Género</p>
                      </div>
                      <div>
                        <p className="text-white font-bold">{selectedApp.audience.interests?.length || 0}</p>
                        <p className="text-zinc-500 text-xs">Intereses</p>
                      </div>
                    </div>
                    {selectedApp.audience.interests?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {selectedApp.audience.interests.map((int, i) => (
                          <span key={i} className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                            {int}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Statistics */}
                {selectedApp.statistics && (
                  <div>
                    <Label className="text-zinc-500 text-xs mb-2 block">Estadísticas</Label>
                    <div className="bg-zinc-800 rounded-lg p-4 grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                      <div>
                        <p className="text-white font-bold">{formatNumber(selectedApp.statistics.average_reach)}</p>
                        <p className="text-zinc-500 text-xs">Alcance promedio</p>
                      </div>
                      <div>
                        <p className="text-white font-bold">{selectedApp.statistics.engagement_rate}%</p>
                        <p className="text-zinc-500 text-xs">Engagement</p>
                      </div>
                      <div>
                        <p className="text-white font-bold">{formatNumber(selectedApp.statistics.average_story_views)}</p>
                        <p className="text-zinc-500 text-xs">Vistas Stories</p>
                      </div>
                      <div>
                        <p className="text-white font-bold">{formatNumber(selectedApp.statistics.monthly_reach)}</p>
                        <p className="text-zinc-500 text-xs">Alcance mensual</p>
                      </div>
                    </div>
                  </div>
                )}

                {selectedApp.message && (
                  <div>
                    <Label className="text-zinc-500 text-xs">Mensaje</Label>
                    <p className="text-white text-sm">{selectedApp.message}</p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Press Detail Modal (with bank info) */}
        <Dialog open={!!selectedPress} onOpenChange={() => setSelectedPress(null)}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center overflow-hidden">
                  {selectedPress?.logo_url ? (
                    <img src={selectedPress.logo_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Newspaper className="text-blue-400" size={20} />
                  )}
                </div>
                {selectedPress?.media_name}
                {selectedPress?.is_verified && <ShieldCheck className="text-blue-400" size={18} />}
              </DialogTitle>
            </DialogHeader>
            
            {selectedPress && (
              <div className="space-y-6 py-4">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-zinc-500">Email</span>
                    <p className="text-white">{selectedPress.email}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Contacto</span>
                    <p className="text-white">{selectedPress.contact_name || '-'}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Teléfono</span>
                    <p className="text-white">{selectedPress.phone || '-'}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Ubicación</span>
                    <p className="text-white">{[selectedPress.city, selectedPress.province, selectedPress.country].filter(Boolean).join(', ') || '-'}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Tipo de Medio</span>
                    <p className="text-white capitalize">{selectedPress.media_type || '-'}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Slug</span>
                    <p className="text-blue-400">/{selectedPress.slug}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Costo Servicio</span>
                    <p className="text-white">{selectedPress.service_fee_percent || 10}%</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Estado</span>
                    <p className={selectedPress.is_active !== false ? 'text-green-400' : 'text-red-400'}>
                      {selectedPress.is_active !== false ? 'Activo' : 'Inactivo'}
                    </p>
                  </div>
                </div>
                
                {selectedPress.description && (
                  <div>
                    <span className="text-zinc-500 text-sm">Descripción</span>
                    <p className="text-white text-sm mt-1">{selectedPress.description}</p>
                  </div>
                )}

                {/* Social Networks */}
                {selectedPress.social_networks?.length > 0 && (
                  <div>
                    <span className="text-zinc-500 text-sm">Redes Sociales</span>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {selectedPress.social_networks.map((sn, i) => (
                        <div key={i} className="bg-zinc-800 rounded-lg p-3 flex items-center gap-3">
                          <Globe className="text-zinc-400" size={16} />
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm capitalize">{sn.platform}</p>
                            <p className="text-zinc-500 text-xs truncate">{sn.username || sn.url}</p>
                          </div>
                          <p className="text-white font-bold text-sm">{formatNumber(sn.followers)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Bank Info Section */}
                <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4">
                  <h4 className="text-sm font-bold text-blue-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <DollarSign size={16} />
                    Datos Bancarios
                  </h4>
                  {selectedPress.bank_info ? (
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-zinc-500">Banco</span>
                        <p className="text-white">{selectedPress.bank_info.bank_name || '-'}</p>
                      </div>
                      <div>
                        <span className="text-zinc-500">Titular</span>
                        <p className="text-white">{selectedPress.bank_info.account_holder || '-'}</p>
                      </div>
                      <div>
                        <span className="text-zinc-500">CBU</span>
                        <p className="text-white font-mono text-xs">{selectedPress.bank_info.cbu || '-'}</p>
                      </div>
                      <div>
                        <span className="text-zinc-500">Alias</span>
                        <p className="text-white">{selectedPress.bank_info.alias || '-'}</p>
                      </div>
                      <div>
                        <span className="text-zinc-500">CUIT</span>
                        <p className="text-white">{selectedPress.bank_info.cuit || '-'}</p>
                      </div>
                      {selectedPress.bank_info.is_locked && (
                        <div>
                          <span className="text-zinc-500">Estado</span>
                          <p className="text-yellow-400 text-xs">Datos bloqueados</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-zinc-500 text-sm">No hay datos bancarios registrados</p>
                  )}
                </div>

                {/* Service Packages */}
                {selectedPress.service_packages?.length > 0 && (
                  <div>
                    <span className="text-zinc-500 text-sm">Paquetes de Servicios</span>
                    <div className="grid gap-2 mt-2">
                      {selectedPress.service_packages.map((pkg, i) => (
                        <div key={i} className="bg-zinc-800 rounded-lg p-3">
                          <div className="flex justify-between items-center">
                            <span className="text-white font-medium">{pkg.name}</span>
                            <span className="text-blue-400 font-bold">${pkg.price?.toLocaleString()}</span>
                          </div>
                          {pkg.description && <p className="text-zinc-500 text-xs mt-1">{pkg.description}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </AdminLayout>
    </SidebarProvider>
  );
};

export default PressAdminPage;
